import { SortDirection } from '../apiInterfaces';
import { SOLVE_V2_BASE_URL } from '../constants';
import { dashboardApi } from '../dashboard-api';
import { DiscoverArticle } from 'src/reducers/discoverReducer/types';
import type {
  BaseContentSnippet,
  ContentSnippet,
  ContentSnippetMetrics,
  ListContentSnippetsResponse,
} from 'src/types/contentSnippetsTypes';

const contentSnippetsApi = dashboardApi.injectEndpoints({
  endpoints: builder => ({
    createContentSnippet: builder.mutation<ContentSnippet, BaseContentSnippet>({
      invalidatesTags: [{ id: 'LIST', type: 'ContentSnippets' }],
      query: body => ({
        body,
        method: 'POST',
        url: `${SOLVE_V2_BASE_URL}/content-snippets`,
      }),
    }),
    createContentSnippetFromArticle: builder.mutation<
      DiscoverArticle,
      {
        articleId: string;
      }
    >({
      invalidatesTags: [{ id: 'LIST', type: 'ContentSnippets' }],
      query: body => ({
        body: { ...body, is_draft: false, publish_as_content_snippet: true },
        method: 'PUT',
        url: `${API_URL}ssel/discover/v1/article/${body.articleId}/publish`,
      }),
    }),
    deleteContentSnippet: builder.mutation<void, string>({
      invalidatesTags: () => [
        // { id, type: 'ContentSnippets' }, - this has to be commented-out
        // It can't be used because of
        // https://github.com/reduxjs/redux-toolkit/issues/1672
        // (the issue was closed but the problem still exists)
        { id: 'LIST', type: 'ContentSnippets' },
      ],
      query: contentSnippetId => ({
        method: 'DELETE',
        url: `${SOLVE_V2_BASE_URL}/content-snippets/${contentSnippetId}`,
      }),
    }),
    discardContentSnippetDraft: builder.mutation<
      void,
      { contentSnippetId: string; isNew: boolean }
    >({
      invalidatesTags: (result, error, { contentSnippetId, isNew }) => [
        ...(isNew
          ? []
          : ([{ id: contentSnippetId, type: 'ContentSnippets' }] as const)),
        { id: 'LIST', type: 'ContentSnippets' },
      ],
      query: ({ contentSnippetId }) => ({
        method: 'POST',
        url: `${SOLVE_V2_BASE_URL}/content-snippets/${contentSnippetId}/discard-draft`,
      }),
    }),
    duplicateContentSnippet: builder.mutation<ContentSnippet, string>({
      invalidatesTags: [{ id: 'LIST', type: 'ContentSnippets' }],
      query: content_snippet_id => ({
        method: 'POST',
        url: `${SOLVE_V2_BASE_URL}/content-snippets/${content_snippet_id}/duplicate`,
      }),
    }),
    getContentSnippetById: builder.query<ContentSnippet, string>({
      providesTags: (result, error, id) => [{ id, type: 'ContentSnippets' }],
      query: contentSnippetId => ({
        url: `${SOLVE_V2_BASE_URL}/content-snippets/${contentSnippetId}`,
      }),
    }),
    getContentSnippetMetrics: builder.query<
      ContentSnippetMetrics,
      { end?: number; start?: number } | void
    >({
      query: params => ({
        method: 'GET',
        params: params || {},
        url: `${SOLVE_V2_BASE_URL}/content-snippets/metrics/aggregate`,
      }),
    }),
    getContentSnippetMetricsPerContentSnippet: builder.query<
      ContentSnippetMetrics,
      { contentSnippetId: string; end?: number; start?: number }
    >({
      query: ({ contentSnippetId, end, start }) => ({
        method: 'GET',
        params: { end, start },
        url: `${SOLVE_V2_BASE_URL}/content-snippets/metrics/${contentSnippetId}`,
      }),
    }),
    listContentSnippets: builder.query<
      ListContentSnippetsResponse,
      {
        draftMode?: 'published' | 'draft' | 'any';
        intent_workflow_id?: string;
        keywords?: string;
        page?: number;
        page_size?: number;
        sort_direction?: SortDirection;
      } | void
    >({
      providesTags: result =>
        result
          ? [
              ...result.snippets.map(
                ({ content_snippet_id }) =>
                  ({
                    id: content_snippet_id,
                    type: 'ContentSnippets',
                  }) as const,
              ),
              { id: 'LIST', type: 'ContentSnippets' },
            ]
          : [{ id: 'LIST', type: 'ContentSnippets' }],
      query: ({
        draftMode = 'published',
        intent_workflow_id,
        keywords,
        page = 1,
        page_size = 5000, // TODO: implement pagination if needed
        sort_direction = 'desc',
      } = {}) => ({
        params: {
          draft_mode: draftMode,
          intent_workflow_id,
          keywords,
          page,
          page_size,
          sort_direction,
        },
        url: `${SOLVE_V2_BASE_URL}/content-snippets`,
      }),
    }),
    publishContentSnippet: builder.mutation<
      ContentSnippet,
      { contentSnippetId: string; isActive?: boolean }
    >({
      invalidatesTags: (result, error, { contentSnippetId }) => [
        { id: contentSnippetId, type: 'ContentSnippets' },
        { id: 'LIST', type: 'ContentSnippets' },
      ],
      query: ({ contentSnippetId, isActive }) => ({
        body: { ...(isActive !== undefined && { is_active: isActive }) },
        method: 'POST',
        url: `${SOLVE_V2_BASE_URL}/content-snippets/${contentSnippetId}/publish`,
      }),
    }),
    setContentSnippetActiveStatus: builder.mutation<
      ContentSnippet,
      { contentSnippetId: string; isActive: boolean }
    >({
      invalidatesTags: (result, error, { contentSnippetId }) => [
        { id: contentSnippetId, type: 'ContentSnippets' },
      ],
      query: ({ contentSnippetId, isActive }) => ({
        body: { is_active: isActive },
        method: 'POST',
        url: `${SOLVE_V2_BASE_URL}/content-snippets/${contentSnippetId}/set-active`,
      }),
    }),
    updateContentSnippet: builder.mutation<
      ContentSnippet,
      { contentSnippetId: string; data: BaseContentSnippet }
    >({
      invalidatesTags: (result, error, { contentSnippetId }) => [
        { id: contentSnippetId, type: 'ContentSnippets' },
      ],
      query: ({ contentSnippetId, data }) => ({
        body: data,
        method: 'POST',
        url: `${SOLVE_V2_BASE_URL}/content-snippets/${contentSnippetId}`,
      }),
    }),
  }),
});

export const {
  useCreateContentSnippetFromArticleMutation,
  useCreateContentSnippetMutation,
  useDeleteContentSnippetMutation,
  useDiscardContentSnippetDraftMutation,
  useDuplicateContentSnippetMutation,
  useGetContentSnippetByIdQuery,
  useGetContentSnippetMetricsPerContentSnippetQuery,
  useGetContentSnippetMetricsQuery,
  useListContentSnippetsQuery,
  usePublishContentSnippetMutation,
  useSetContentSnippetActiveStatusMutation,
  useUpdateContentSnippetMutation,
} = contentSnippetsApi;
